import { toDecimal } from './money';
import type { AssetType, CurrencyCode, Holding } from './types';

/**
 * ตรวจและทำให้ input จากฟอร์มเป็นมาตรฐานก่อนเขียนลง Firestore
 *
 * อยู่ใน core ไม่ใช่ใน web เพราะ `dailySnapshot` ฝั่ง function ก็อ่าน holding เหมือนกัน —
 * กติกาว่าอะไรคือ holding ที่ถูกต้อง ต้องมีนิยามเดียวทั้งสองฝั่ง
 */

export class HoldingValidationError extends Error {
  constructor(
    readonly field: string,
    reason: string,
  ) {
    super(reason);
    this.name = 'HoldingValidationError';
  }
}

/** ข้อมูลที่ผู้ใช้กรอก — ก่อนผ่านการตรวจ */
export interface HoldingDraft {
  platform: string;
  assetType: AssetType;
  symbol: string;
  displayName?: string | null;
  quantity: string;
  avgCost: string;
  currency: string;
  note?: string | null;
  /** รหัสเหรียญของ CoinGecko เช่น `bitcoin` — บังคับเมื่อ assetType เป็น crypto */
  coingeckoId?: string | null;
}

/** ผลลัพธ์หลังตรวจแล้ว — พร้อมเขียนลง Firestore (ยังไม่มี id/timestamp) */
export type ValidatedHolding = Omit<Holding, 'id'>;

/**
 * รายชื่อประเภทสินทรัพย์ที่ระบบรู้จัก — **แหล่งความจริงเดียว**
 *
 * export ออกไปเพราะฝั่งเว็บต้องใช้ตรวจข้อมูลที่อ่านจาก Firestore ด้วย
 * เคยเขียนรายการนี้ซ้ำสองที่แล้วลืมอัปเดตฝั่งหนึ่งตอนเพิ่ม `cash` — พอร์ตโหลดไม่ขึ้นทันที
 */
export const ASSET_TYPES: readonly AssetType[] = ['stock', 'etf', 'crypto', 'cash'];

/** ราคาของเงินสด 1 หน่วย ในสกุลของตัวมันเอง — คงที่ตลอด */
export const CASH_UNIT_COST = '1';

/** ticker ของหุ้น/ETF: ตัวอักษร ตัวเลข จุด ขีด — ครอบคลุมทั้ง VOO, BRK.B, RDS-A */
const SYMBOL_PATTERN = /^[A-Z0-9.-]{1,20}$/;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;
/** รหัสเหรียญของ CoinGecko เป็น slug ตัวพิมพ์เล็ก เช่น bitcoin, matic-network */
const COINGECKO_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,49}$/;

/**
 * สัญลักษณ์นี้เก็บลงพอร์ตได้ไหม — ใช้กรองผลค้นหาก่อนเอาไปโชว์ให้ผู้ใช้เลือก
 *
 * ผลค้นหาจาก Yahoo มีทั้ง futures (`GC=F`) และดัชนี (`^GSPC`) ซึ่ง `SYMBOL_PATTERN`
 * ไม่รับ ถ้าปล่อยให้เลือกได้ ผู้ใช้จะเจอ error ตอนกดบันทึกโดยไม่มีทางรู้ล่วงหน้า
 * ว่าอันไหนเลือกได้ — คัดออกตั้งแต่ตอนแสดงผลจึงตรงกว่า
 *
 * อยู่ที่นี่เพราะเกณฑ์ต้องเป็นตัวเดียวกับที่ `validateHoldingDraft` ใช้เสมอ
 * เขียนซ้ำฝั่ง functions เมื่อไหร่ วันหนึ่งสองฝั่งจะไม่ตรงกัน
 */
export function isStorableSymbol(symbol: string): boolean {
  return SYMBOL_PATTERN.test(symbol.trim().toUpperCase());
}

/** รหัสเหรียญนี้เก็บลงพอร์ตได้ไหม — เหตุผลเดียวกับ `isStorableSymbol` */
export function isStorableCoingeckoId(id: string): boolean {
  return COINGECKO_ID_PATTERN.test(id.trim().toLowerCase());
}

const MAX_PLATFORM_LENGTH = 60;
const MAX_DISPLAY_NAME_LENGTH = 120;
const MAX_NOTE_LENGTH = 500;

function requireText(value: string, field: string, maxLength: number, label: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new HoldingValidationError(field, `กรุณากรอก${label}`);
  }
  if (trimmed.length > maxLength) {
    throw new HoldingValidationError(field, `${label}ยาวเกิน ${maxLength} ตัวอักษร`);
  }
  return trimmed;
}

function optionalText(
  value: string | null | undefined,
  field: string,
  maxLength: number,
  label: string,
): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (trimmed.length > maxLength) {
    throw new HoldingValidationError(field, `${label}ยาวเกิน ${maxLength} ตัวอักษร`);
  }
  return trimmed;
}

/**
 * สร้าง `priceKey` ที่ใช้ชี้ไปยัง cache ราคา — spec Section 4.5
 *
 * คริปโตต้องใช้รหัสเหรียญของ CoinGecko ไม่ใช่ ticker เพราะ CoinGecko อ้างอิงด้วย slug
 * (`BTC` ไม่ใช่ key ที่ใช้ได้ ต้องเป็น `bitcoin`) — จึงบังคับให้ระบุมา ไม่เดาให้
 */
export function buildPriceKey(input: {
  assetType: AssetType;
  symbol: string;
  coingeckoId?: string | null;
}): string {
  // เงินสดไม่ต้องไปถามราคาจากที่ไหน ตัว key มีไว้แค่ให้รูปแบบข้อมูลเหมือนกันทั้งระบบ
  if (input.assetType === 'cash') {
    return `cash:${input.symbol.trim().toUpperCase()}`;
  }

  if (input.assetType === 'crypto') {
    const id = (input.coingeckoId ?? '').trim().toLowerCase();
    if (id.length === 0) {
      throw new HoldingValidationError(
        'coingeckoId',
        'คริปโตต้องระบุรหัสเหรียญของ CoinGecko เช่น bitcoin',
      );
    }
    if (!COINGECKO_ID_PATTERN.test(id)) {
      throw new HoldingValidationError(
        'coingeckoId',
        'รหัสเหรียญของ CoinGecko ต้องเป็นตัวพิมพ์เล็ก ตัวเลข หรือขีดกลางเท่านั้น',
      );
    }
    return `coingecko:${id}`;
  }
  return `yahoo:${input.symbol.trim().toUpperCase()}`;
}

/** ตรวจ + ทำให้เป็นมาตรฐาน — throw `HoldingValidationError` พร้อมชื่อ field ที่ผิด */
export function validateHoldingDraft(draft: HoldingDraft): ValidatedHolding {
  const platform = requireText(draft.platform, 'platform', MAX_PLATFORM_LENGTH, 'ชื่อแพลตฟอร์ม');

  if (!ASSET_TYPES.includes(draft.assetType)) {
    throw new HoldingValidationError('assetType', 'ประเภทสินทรัพย์ไม่ถูกต้อง');
  }

  // เงินสดใช้รหัสสกุลเงินเป็นสัญลักษณ์ในตัว — ผู้ใช้กรอกแค่สกุลเงินกับจำนวน
  if (draft.assetType === 'cash') {
    return validateCashDraft(draft);
  }

  const symbol = requireText(draft.symbol, 'symbol', 20, 'สัญลักษณ์').toUpperCase();
  if (!SYMBOL_PATTERN.test(symbol)) {
    throw new HoldingValidationError(
      'symbol',
      'สัญลักษณ์ใช้ได้เฉพาะตัวอักษร ตัวเลข จุด และขีดกลาง',
    );
  }

  const quantity = parsePositive(draft.quantity, 'quantity', 'จำนวน');
  const avgCost = parseNonNegative(draft.avgCost, 'avgCost', 'ต้นทุนเฉลี่ย');

  const currency = draft.currency.trim().toUpperCase() as CurrencyCode;
  if (!CURRENCY_PATTERN.test(currency)) {
    throw new HoldingValidationError('currency', 'สกุลเงินต้องเป็นรหัส 3 ตัวอักษร เช่น USD');
  }

  return {
    platform,
    assetType: draft.assetType,
    symbol,
    // ไม่ได้ตั้งชื่อเต็มก็ใช้สัญลักษณ์แทน จะได้ไม่มีแถวไหนแสดงเป็นช่องว่าง
    displayName:
      optionalText(draft.displayName, 'displayName', MAX_DISPLAY_NAME_LENGTH, 'ชื่อ') ?? symbol,
    quantity,
    avgCost,
    currency,
    priceKey: buildPriceKey({ assetType: draft.assetType, symbol, coingeckoId: draft.coingeckoId }),
    note: optionalText(draft.note, 'note', MAX_NOTE_LENGTH, 'โน้ต'),
  };
}

/**
 * เงินสดมีแค่สองอย่างที่ต้องรู้: สกุลเงินอะไร และมีเท่าไหร่
 *
 * ต้นทุนต่อหน่วยของเงินสดคือ 1 เสมอโดยนิยาม จึงไม่ให้กรอก — ปิดช่องที่กรอกผิดได้ทิ้งไปเลย
 * ผลคือ PnL ของเงินสดเป็นศูนย์ ซึ่งถูกต้อง เงินสดไม่สร้างผลตอบแทน
 */
function validateCashDraft(draft: HoldingDraft): ValidatedHolding {
  const platform = requireText(draft.platform, 'platform', MAX_PLATFORM_LENGTH, 'ชื่อแพลตฟอร์ม');

  const currency = draft.currency.trim().toUpperCase() as CurrencyCode;
  if (!CURRENCY_PATTERN.test(currency)) {
    throw new HoldingValidationError('currency', 'สกุลเงินต้องเป็นรหัส 3 ตัวอักษร เช่น USD');
  }

  const quantity = parsePositive(draft.quantity, 'quantity', 'จำนวนเงิน');

  return {
    platform,
    assetType: 'cash',
    symbol: currency,
    displayName:
      optionalText(draft.displayName, 'displayName', MAX_DISPLAY_NAME_LENGTH, 'ชื่อ') ??
      `เงินสด ${currency}`,
    quantity,
    avgCost: CASH_UNIT_COST,
    currency,
    priceKey: buildPriceKey({ assetType: 'cash', symbol: currency }),
    note: optionalText(draft.note, 'note', MAX_NOTE_LENGTH, 'โน้ต'),
  };
}

function parsePositive(raw: string, field: string, label: string): string {
  const value = parseDecimalField(raw, field, label);
  if (value.lessThanOrEqualTo(0)) {
    throw new HoldingValidationError(field, `${label}ต้องมากกว่า 0`);
  }
  return value.toFixed();
}

function parseNonNegative(raw: string, field: string, label: string): string {
  const value = parseDecimalField(raw, field, label);
  if (value.isNegative()) {
    throw new HoldingValidationError(field, `${label}ต้องไม่ติดลบ`);
  }
  return value.toFixed();
}

function parseDecimalField(raw: string, field: string, label: string) {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    throw new HoldingValidationError(field, `กรุณากรอก${label}`);
  }
  try {
    return toDecimal(trimmed, field);
  } catch {
    // ผู้ใช้พิมพ์ "1,000" หรือ "1e5" มาก็ตกที่นี่ — บอกให้ชัดว่าต้องพิมพ์ยังไง
    throw new HoldingValidationError(field, `${label}ต้องเป็นตัวเลข ไม่ใส่ comma`);
  }
}
